"use client";

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useState } from "react";
import { API } from "@/lib/api/endpoint.common";
import { useChannel } from "@/lib/api/prefix.client";
import { cachedFetch } from "@/lib/fetch-cache";
import { useGameLocale } from "@/lib/i18n";
import { GAME_ROOT, lookupGameMessage, type GameMessages, type GameValues } from "./game-messages.common";
export type TryGameT = (key: string, values?: GameValues) => string | undefined;
export function useGameTranslations(namespace) {
  return useTranslations(namespace ? `${GAME_ROOT}.${namespace}` : GAME_ROOT);
}

/** The game's own string for a key, or undefined when the page's pack has
 * none; callers pick their own fallback. */
export function useTryGameTranslations(namespace) {
  const $ = _c(2);
  const t = useGameTranslations(namespace);
  let t0;
  if ($[0] !== t) {
    t0 = (key, values) => lookupGameMessage(t, key, values);
    $[0] = t;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
const EMPTY: GameMessages = {};

/** Whole localization tables fetched in one request for pages whose ids only
 * exist client side. Returns {} until they arrive. */
export function useGameTables(tables, beta) {
  const $ = _c(5);
  const lang = useGameLocale();
  const channel = useChannel(beta);
  let t0;
  if ($[0] !== tables) {
    t0 = [...new Set(tables)].sort().join(",");
    $[0] = tables;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const names = t0;
  const url = names ? `${API}/api/localizations?tables=${encodeURIComponent(names)}&lang=${lang}&channel=${channel}` : "";
  const [loaded, setLoaded] = useState(null);
  let t1;
  let t2;
  if ($[2] !== url) {
    t1 = () => {
      if (!url) {
        return;
      }
      let cancelled = false;
      cachedFetch(url).then(messages => {
        if (!cancelled) {
          setLoaded({
            url,
            messages
          });
        }
      }).catch(_temp);
      return () => {
        cancelled = true;
      };
    };
    t2 = [url];
    $[2] = url;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return loaded?.url === url ? loaded.messages : EMPTY;
}
function _temp() {}